import React, { useEffect, useState } from 'react';
import { Routes, Route, useNavigate, useLocation } from 'react-router-dom';
import { FiMenu, FiX, FiArrowLeft } from 'react-icons/fi';
import { toast } from 'react-toastify';
import Sidebar from '../components/Sidebar/Sidebar.jsx';
import ChatArea from '../components/Chat/ChatArea.jsx';
import AdminPanel from './AdminPanel.jsx';
import ProjectManagement from '../components/Projects/ProjectManagement.jsx';
import EODReports from '../components/EOD/EODReports.jsx';
import VideoCall from '../components/VideoCall/VideoCall.jsx';
import PushNotifications from '../components/PushNotifications/PushNotifications.jsx';
import LeaveRequest from '../components/Leaves/LeaveRequest.jsx';
import LeaveManagement from '../components/Leaves/LeaveManagement.jsx';
import MyProfile from '../components/Profile/MyProfile.jsx';
import UserDetailView from '../components/Profile/UserDetailView.jsx';
import EmployeeProfileEditor from '../components/Profile/EmployeeProfileEditor.jsx';
import AttendanceDashboard from '../components/Attendance/AttendanceDashboard.jsx';
import AttendanceTracker from '../components/Attendance/AttendanceTracker.jsx';
import MyAttendanceWithTeam from '../components/Attendance/MyAttendanceWithTeam.jsx';
import TicketManagement from '../components/Tickets/TicketManagement.jsx';
import ClientPortal from '../components/Tickets/ClientPortal.jsx';
import MobileChatHome from '../components/MobileChatHome/MobileChatHome.jsx';
import MobileBottomNav from '../components/MobileBottomNav/MobileBottomNav.jsx';
import { useChatStore } from '../store/chatStore';
import { getSocket } from '../services/socket';
import { useAuthStore } from '../store/authStore';
import Logo from '../assets/LogoBlack.png';
import './Dashboard.css';

const PAGE_TITLES = [
  ['/dashboard/admin', 'Admin Panel'],
  ['/dashboard/projects', 'Projects'],
  ['/dashboard/attendance', 'Attendance'],
  ['/dashboard/eod', 'EOD Reports'],
  ['/dashboard/leaves', 'Leaves'],
  ['/dashboard/profile', 'My Profile'],
  ['/dashboard/user-detail', 'Profile'],
  ['/dashboard/edit-profile', 'Edit Profile'],
  ['/dashboard/tickets', 'Tickets'],
];

function Dashboard() {
  const { fetchChannels, addMessage, updateMessage, deleteMessage, setOnlineUsers, updateUserStatus, removeChannel, setCallStatus } = useChatStore();
  const { user } = useAuthStore();
  const navigate = useNavigate();
  const [incomingCall, setIncomingCall] = useState(null);
  const [sidebarOpen, setSidebarOpen] = useState(false);
  const { pathname } = useLocation();

  // Mobile (WhatsApp-style): home shows the chat list full screen, an open chat takes the whole screen
  const isChatHome = user?.role !== 'client' && /^\/dashboard\/?$/.test(pathname);
  const isChatOpen = pathname.startsWith('/dashboard/channel/');
  const mobileViewClass = isChatHome ? 'mobile-chat-home' : isChatOpen ? 'mobile-chat-open' : '';
  // Bottom tab bar everywhere except full-screen chat/call (they own the bottom of the screen)
  const showBottomNav = !isChatOpen && !pathname.startsWith('/dashboard/call/');
  // Inner pages (attendance, leaves, profile...) get a back arrow to the chat list instead of the hamburger
  const pageTitle = user?.role !== 'client' && PAGE_TITLES.find(([prefix]) => pathname.startsWith(prefix))?.[1];

  useEffect(() => {
    fetchChannels();

    const socket = getSocket();
    if (socket) {
      // Listen for new messages
      socket.on('message:new', (message) => {
        console.log('New message received via socket:', message);
        addMessage(message);
        // Refresh channels to update unread counts
        fetchChannels();
      });

      // Listen for message updates
      socket.on('message:updated', (message) => {
        updateMessage(message);
      });

      // Listen for message deletions
      socket.on('message:deleted', ({ messageId }) => {
        deleteMessage(messageId);
      });

      // Listen for online users
      socket.on('users:online', (users) => {
        setOnlineUsers(users);
      });

      // Listen for user status changes
      socket.on('user:status', ({ userId, status }) => {
        updateUserStatus(userId, status);
      });

      // Listen for new channels (including DMs)
      socket.on('channel:created', (channel) => {
        console.log('New channel created:', channel);
        fetchChannels(); // Refresh channels list
      });

      // Listen for channel deletion
      socket.on('channel:deleted', ({ channelId }) => {
        console.log('Channel deleted:', channelId);
        removeChannel(channelId); // Remove from state immediately
        // Navigate away if we were viewing the deleted channel
        if (window.location.pathname.includes(channelId)) {
          navigate('/dashboard');
        }
      });

      // Listen for incoming calls
      socket.on('call:incoming', ({ channelId, callerId, callerName, callType }) => {
        console.log('Incoming call from:', callerName, 'Type:', callType);
        // Already inside this call (e.g. joined from a notification) — don't ring again
        if (window.location.pathname.includes(`/call/${channelId}`)) return;
        setIncomingCall({ channelId, callerId, callerName, callType });
      });

      // Which channels have a call running (for the "Join call" button)
      socket.on('call:status', setCallStatus);

      // Caller hung up before we answered — stop ringing
      socket.on('call:cancelled', ({ channelId, callerName }) => {
        setIncomingCall((current) => {
          if (current?.channelId !== channelId) return current;
          toast.info(`Missed call from ${callerName || current.callerName}`, { toastId: `missed-${channelId}` });
          return null;
        });
      });

      // Leave requests (for approvers) and decisions (for the employee)
      const showLeaveToast = (payload) => {
        const show = payload?.title?.includes('Rejected') ? toast.error : toast.info;
        show(`${payload.title}: ${payload.body}`, {
          toastId: payload.tag,
          autoClose: 8000,
          onClick: () => navigate('/dashboard/leaves')
        });
      };
      socket.on('leave:new', showLeaveToast);
      socket.on('leave:status', showLeaveToast);

      return () => {
        socket.off('leave:new', showLeaveToast);
        socket.off('leave:status', showLeaveToast);
        socket.off('message:new');
        socket.off('message:updated');
        socket.off('message:deleted');
        socket.off('users:online');
        socket.off('user:status');
        socket.off('channel:created');
        socket.off('channel:deleted');
        socket.off('call:incoming');
        socket.off('call:cancelled');
        socket.off('call:status', setCallStatus);
      };
    }
  }, [fetchChannels, addMessage, updateMessage, deleteMessage, setOnlineUsers, updateUserStatus, removeChannel, setCallStatus, navigate]);

  // Stop ringing after 45s if nobody answers
  useEffect(() => {
    if (!incomingCall) return undefined;
    const timer = setTimeout(() => {
      toast.info(`Missed call from ${incomingCall.callerName}`, { toastId: `missed-${incomingCall.channelId}` });
      setIncomingCall(null);
    }, 45 * 1000);
    return () => clearTimeout(timer);
  }, [incomingCall]);

  const handleAcceptCall = () => {
    if (incomingCall) {
      navigate(`/dashboard/call/${incomingCall.channelId}?type=${incomingCall.callType}`);
      setIncomingCall(null);
    }
  };

  const handleRejectCall = () => {
    if (incomingCall) {
      const socket = getSocket();
      socket?.emit('call:reject', { channelId: incomingCall.channelId });
      setIncomingCall(null);
    }
  };

  return (
    <div className={`dashboard-container ${mobileViewClass} ${showBottomNav ? 'has-bottom-nav' : ''}`}>
      {/* Mobile Menu Toggle */}
      <div className="mobile-menu-toggle">
        {pageTitle ? (
          <>
            <button
              className="menu-toggle-btn"
              onClick={() => navigate('/dashboard')}
              aria-label="Back to chats"
            >
              <FiArrowLeft size={22} />
            </button>
            <h1 className="mobile-page-title">{pageTitle}</h1>
          </>
        ) : (
          <>
            <button
              className="menu-toggle-btn"
              onClick={() => setSidebarOpen(!sidebarOpen)}
              aria-label={sidebarOpen ? 'Close menu' : 'Open menu'}
            >
              {sidebarOpen ? <FiX size={22} /> : <FiMenu size={22} />}
            </button>
            <img src={Logo} alt="Bigwals" className="mobile-logo" />
          </>
        )}
      </div>

      {/* WhatsApp-style chat list, shown instead of the sidebar on mobile home */}
      {isChatHome && (
        <div className="mobile-home-screen">
          <MobileChatHome />
        </div>
      )}

      {/* Sidebar with overlay */}
      <div className={`sidebar-wrapper ${sidebarOpen ? 'active' : ''}`}>
        <Sidebar onClose={() => setSidebarOpen(false)} />
        {/* Overlay for mobile */}
        {sidebarOpen && (
          <div 
            className="sidebar-overlay" 
            onClick={() => setSidebarOpen(false)}
          />
        )}
      </div>

      <div className="dashboard-main">
        <Routes>
          <Route path="/" element={<ChatArea />} />
          <Route path="/channel/:channelId" element={<ChatArea />} />
          {(['ceo', 'co_ceo', 'company_manager'].includes(user?.role)) && (
            <Route path="/admin" element={<AdminPanel />} />
          )}
          {(['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'manager'].includes(user?.role)) && (
            <Route path="/projects" element={<ProjectManagement />} />
          )}
          {(['ceo', 'co_ceo', 'company_manager', 'production_head', 'attendance_manager'].includes(user?.role)) && (
            <Route path="/attendance" element={<AttendanceDashboard />} />
          )}
          {user?.role === 'sales_head' && (
            <Route path="/attendance" element={<MyAttendanceWithTeam />} />
          )}
          {!(['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'attendance_manager', 'client'].includes(user?.role)) && (
            <Route path="/attendance" element={<AttendanceTracker />} />
          )}
          <Route path="/eod" element={<EODReports />} />
          <Route path="/leaves" element={
            ['ceo', 'co_ceo', 'company_manager', 'production_head', 'manager'].includes(user?.role)
              ? <LeaveManagement /> 
              : <LeaveRequest />
          } />
          <Route path="/profile" element={<MyProfile />} />
          <Route path="/user-detail/:userId" element={<UserDetailView />} />
          {(['ceo', 'co_ceo', 'company_manager'].includes(user?.role)) && (
            <Route path="/edit-profile/:userId" element={<EmployeeProfileEditor />} />
          )}
          <Route path="/call/:channelId" element={<VideoCall />} />
          {(['ceo', 'co_ceo', 'company_manager', 'sales_head', 'production_head', 'manager', 'employee', 'sales_employee', 'production_employee'].includes(user?.role)) && (
            <Route path="/tickets" element={<TicketManagement />} />
          )}
          {user?.role === 'client' && (
            <Route path="/tickets" element={<ClientPortal />} />
          )}
          {user?.role === 'client' && (
            <Route path="/" element={<ClientPortal />} />
          )}
        </Routes>
      </div>
      
      {showBottomNav && <MobileBottomNav />}

      {/* Push Notifications Component */}
      <PushNotifications />
      
      {/* Incoming Call Modal */}
      {incomingCall && (
        <div className="incoming-call-modal">
          <div className="incoming-call-content">
            <div className="incoming-call-header">
              <h3>Incoming {incomingCall.callType === 'audio' ? 'Voice' : 'Video'} Call</h3>
            </div>
            <div className="incoming-call-body">
              <p className="caller-name">{incomingCall.callerName}</p>
              <p className="call-type">is calling you...</p>
            </div>
            <div className="incoming-call-actions">
              <button className="reject-btn" onClick={handleRejectCall}>
                Decline
              </button>
              <button className="accept-btn" onClick={handleAcceptCall}>
                Accept
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}

export default Dashboard;
